import {
  ApplicationConfig,
  EnvironmentProviders,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  Provider,
  provideZoneChangeDetection,
} from "@angular/core";
import * as ngAnimationsAsync from "@angular/platform-browser/animations/async";
import { provideTransloco } from "@jsverse/transloco";
import { AppTranslocoLoader } from "@core/services/translation/transloco.loader";
import { TranslationService } from "@core/services/translation/translation.service";
import {
  DEFAULT_LANGUAGE_CODE,
  TRANSLATED_LANGUAGE_CODE_LIST,
} from "@core/constants/languages";
import { provideRouter } from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { providePrimeNG } from "primeng/config";
import Aura from "@primeng/themes/aura";

import { routes } from "./app.routes";
import { loaderInterceptor } from "@core/interceptors/loader/loader.interceptor";
import { requestInterceptor } from "@core/interceptors/request/request.interceptor";
import { MessageService } from "primeng/api";
import { responseInterceptor } from "@core/interceptors/response/response.interceptor";
import { AuthService } from "@core/services/auth/auth.service";
import { ProtectedRoutesLoaderService } from "@core/routes/protected-routes-loader.service";
import { ERROR_REPORTER, LoggerService } from "@core/logging/logger.service";
import { ErrorReporterService } from "@core/logging/error-reporter.service";
import { GlobalErrorHandler } from "@core/logging/global-error-handler";
import { NavigationBreadcrumbsService } from "@core/logging/navigation-breadcrumbs.service";
import { registerWindowErrorListeners } from "@core/logging/window-error-listeners";
import { PerformanceMonitorService } from "@core/logging/performance-monitor.service";

function provideAnimationsAsyncCompat(): Provider[] {
  const fn = (ngAnimationsAsync as Record<string, unknown>)["provideAnimationsAsync"] as
    | ((type?: "animations" | "noop") => EnvironmentProviders)
    | undefined;
  return fn ? ([fn("animations")] as unknown as Provider[]) : [];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Removed global PreloadAllModules to prevent protected chunks loading pre-auth.
    provideRouter(routes),
    provideAnimationsAsyncCompat(),
    provideHttpClient(
      withInterceptors([
        requestInterceptor,
        loaderInterceptor,
        responseInterceptor,
      ]),
    ),
    MessageService,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Activates LoggerService's remote forwarding (ERROR/FATAL → GCP Error
    // Reporting when LOG_CONFIG.remote is true).
    { provide: ERROR_REPORTER, useExisting: ErrorReporterService },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: ".app-dark",
        },
      },
    }),
    provideTransloco({
      config: {
        availableLangs: TRANSLATED_LANGUAGE_CODE_LIST,
        defaultLang: DEFAULT_LANGUAGE_CODE,
        // Used only when a language *catalog* fails to load (not for missing keys).
        // Missing keys use the static English string passed to | translocoDefault.
        fallbackLang: DEFAULT_LANGUAGE_CODE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          // Do not substitute en-US.json when a key is missing in e.g. de-DE.
          // HTML/static English fallback is applied by translateOr / translocoDefault.
          useFallbackTranslation: false,
        },
      },
      loader: AppTranslocoLoader,
    }),
    provideAppInitializer(() => inject(TranslationService).init()),
    provideAppInitializer(async () => {
      const auth = inject(AuthService);
      const loader = inject(ProtectedRoutesLoaderService);
      const logger = inject(LoggerService);
      const log = logger.createContext("AppInit");
      // Start breadcrumb/session-age collection from app start, and catch
      // out-of-zone failures (e.g. Gigya) from the first moment.
      inject(NavigationBreadcrumbsService);
      registerWindowErrorListeners(logger);
      // Start RUM collection (Web Vitals, route timing, long tasks, TTI).
      // No-op unless LOG_CONFIG.remote is true.
      inject(PerformanceMonitorService).init();
      try {
        if (await auth.isSessionValid()) {
          await loader.loadProtectedRoutes();
        }
      } catch (error) {
        log.error("Application initialization failed", error);
      }
    }),
  ],
};
